import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from './ui/sheet';
import { loadNameMeanings, extractNames, loadProfile } from '../utils/nameMeanings';
import ScriptureRefChips from './ScriptureRefChips';

const ChapterNameItem = ({ item, t, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !profile && !loading) {
      setLoading(true);
      setProfile(await loadProfile(item.name));
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-3" data-testid={`chapter-name-${item.slug}`}>
      <p className="font-semibold text-sm">{item.name}</p>
      <p className="text-xs text-muted-foreground italic mt-0.5">"{item.meaning}"</p>
      <button
        onClick={toggle}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline"
        data-testid={`chapter-name-profile-${item.slug}`}
      >
        <BookOpen className="w-3.5 h-3.5" /> {open ? t('Hide profile', 'Ocultar perfil') : t('View profile', 'Ver perfil')}
      </button>
      {open && (
        <div className="mt-2 border-t pt-2">
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
            : (
              <>
                <p className="text-xs text-foreground/90 max-h-40 overflow-y-auto leading-relaxed">{profile?.definition}</p>
                <ScriptureRefChips refs={profile?.refs} onNavigate={onNavigate} />
              </>
            )}
        </div>
      )}
    </div>
  );
};

/**
 * ChapterNamesPanel — a one-tap summary of every recognized biblical name in
 * the current chapter, with its meaning and an expandable Easton's profile.
 */
const ChapterNamesPanel = ({ verses = [], reference = '', language = 'en', onNavigate }) => {
  const t = (en, es) => (language === 'es' ? es : en);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    loadNameMeanings().then((m) => {
      const text = verses.map((v) => (v.text || '').replace(/<[^>]+>/g, '')).join(' ');
      setItems(extractNames(text, m).sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    });
  }, [open, verses]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-xs border-amber-300/60 dark:border-amber-700/60 text-amber-800 dark:text-amber-200"
          data-testid="chapter-names-btn"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1" />
          {t('Names in this chapter', 'Nombres del capítulo')}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="chapter-names-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            {t('Names in', 'Nombres en')} {reference}
          </SheetTitle>
          <SheetDescription>
            {t("Meanings from Hitchcock's Bible Names.", 'Significados de los Nombres Bíblicos de Hitchcock.')}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              {t('No recognized names in this chapter.', 'No hay nombres reconocidos en este capítulo.')}
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {items.length} {t('names', 'nombres')}
              </p>
              {items.map((it) => (
                <ChapterNameItem key={it.slug} item={it} t={t} onNavigate={(book, chapter) => { setOpen(false); onNavigate && onNavigate(book, chapter); }} />
              ))}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ChapterNamesPanel;
